import React, { createContext, useContext } from 'react';

/**
 * Top-level context that wires the manage-translations form (deep in extension
 * config panels) to the action bar (sibling of the config panels in the tree).
 *
 * Because `ManageTranslationsOpenProvider` is mounted once per extension config
 * panel (inside the editor extension framework), it cannot be a direct ancestor
 * of the action bar. This context is provided at the `HubInfo` level — which IS
 * a common ancestor — and is read by `ManageTranslationsOpenProvider` so changes
 * in any extension's translation form bubble up to where the action bar lives.
 */

/**
 * Metadata and field values for a single translated block instance.
 */
export type StoredTranslationBlock = {
	/** ADF extension type (e.g. `"com.atlassian.confluence.carousel"`). */
	extensionType: string;
	/** fieldPath → translated value. */
	fields: Record<string, string>;
};

export type StoredTranslationsMap = Record<string, Record<string, StoredTranslationBlock>>;

export type HubTranslationsContextValue = {
	/** Locales that have been modified in the current editing session (unsaved changes). */
	defaultLocale?: string;
	defaultLocaleDisplayName?: string;
	dirtyLocales: ReadonlySet<string>;
	/** Mark a locale as having unsaved changes. */
	markLocaleDirty: (locale: string) => void;
	onHasChanges: (hasChanges: boolean) => void;
	/** Clear all dirty-locale tracking after a successful publish. */
	resetDirtyLocales: () => void;
	setPageIsDirty: (isDirty: boolean) => void;
	setStoredTranslations: React.Dispatch<React.SetStateAction<StoredTranslationsMap>>;
	storedTranslations: StoredTranslationsMap;
	supportedLanguages?: Record<string, { localeDisplayName: string }>;
};

export const HubTranslationsContext: React.Context<HubTranslationsContextValue | null> =
	createContext<HubTranslationsContextValue | null>(null);

export const useHubTranslationsContext = (): HubTranslationsContextValue | null =>
	useContext(HubTranslationsContext);
